import React, { useCallback, useEffect, useMemo, useRef, type ElementRef } from 'react';
import { styled } from '@compiled/react';
import kebabCase from 'lodash/kebabCase';
import Button from '@atlaskit/button';
import { Field } from '@atlaskit/form';
import { Box, Text } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import Select from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import type { ProjectStyle } from '../../../../common/constants/project';
import type { ThirdPartyEntity } from '../../../../common/types';
import { customComponents } from '../../../../common/ui/custom-select-components';
import {
	convertOptionToEntity,
	getSelectedAndAllOptions,
	type OptionType,
} from '../../../../common/ui/custom-select-components/utils';
import messages from './messages';

export type Props = {
	label: string;
	placeholder: string;
	starredOptionsLabel: string;
	otherOptionsLabel: string;
	noEntitiesFoundHeader: string;
	noEntitiesFoundDescription: string;
	errorOccurredHeader: string;
	onRetry: () => void;
	entities: ThirdPartyEntity[] | null;
	selectedEntityId: string | null;
	isLoading: boolean;
	isDisabled?: boolean;
	isRequired?: boolean;
	autoFocus?: boolean;
	isClearable?: boolean;
	openMenuOnFocus?: boolean;
	hasErrorOccurred: boolean;
	onChangeOld: (entityId: string, entityName: string, style?: ProjectStyle) => void;
	onChange: (selectedEntity: ThirdPartyEntity) => void;
	placeholderColor?: string;
	onClear?: () => void;
	preselectOnlyOption?: boolean;
};

export const EntitySelector = ({
	label,
	placeholder,
	entities,
	selectedEntityId,
	isLoading,
	isDisabled = false,
	autoFocus = false,
	openMenuOnFocus = false,
	isClearable = false,
	onChange,
	onChangeOld,
	onClear,
	hasErrorOccurred,
	starredOptionsLabel,
	otherOptionsLabel,
	noEntitiesFoundHeader,
	noEntitiesFoundDescription,
	errorOccurredHeader,
	onRetry,
	placeholderColor,
	isRequired,
	preselectOnlyOption,
}: Props) => {
	const { formatMessage } = useIntl();
	const selectRef = useRef<ElementRef<typeof Select<OptionType>>>(null);
	const onChangeHandler = useCallback(
		(option: OptionType | null) => {
			if (option === null && onClear) {
				onClear();
				return;
			}
			if (fg('jira_importer_third_party_state_refactor_2')) {
				option && onChange(convertOptionToEntity(option));
			} else {
				option && onChangeOld(option.value, option.label, option.style);
			}
		},
		[onChange, onChangeOld, onClear],
	);

	const { selectedOption, options } = useMemo(
		() =>
			getSelectedAndAllOptions(
				entities,
				selectedEntityId,
				starredOptionsLabel,
				otherOptionsLabel,
				placeholderColor,
			),
		[entities, otherOptionsLabel, placeholderColor, selectedEntityId, starredOptionsLabel],
	);

	useEffect(() => {
		// preselect the only option if there is only one option available
		if (
			preselectOnlyOption &&
			selectRef.current &&
			!isLoading &&
			!selectedOption &&
			options?.length === 1 &&
			'value' in options[0]
		) {
			onChangeHandler(options[0]);
			selectRef.current.blur();
		}
	}, [isLoading, onChangeHandler, options, preselectOnlyOption, selectedOption]);

	if (!isLoading && entities && entities.length === 0) {
		return (
			<Box paddingBlockStart="space.200">
				<SectionMessage title={noEntitiesFoundHeader} appearance="warning">
					<Text>{noEntitiesFoundDescription}</Text>
				</SectionMessage>
			</Box>
		);
	}
	return (
		<Wrapper data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.entity-content.entity-selector.wrapper">
			<Field label={label} name={`selector-${kebabCase(label)}`} isRequired={isRequired}>
				{({ fieldProps }) => (
					<>
						<Select<OptionType>
							{...fieldProps}
							inputId={`entityDropdown-${kebabCase(label)}`}
							name="entityDropdown"
							options={options}
							value={selectedOption}
							onChange={onChangeHandler}
							placeholder={placeholder}
							isClearable={isClearable}
							isLoading={isLoading}
							isDisabled={isDisabled || hasErrorOccurred}
							autoFocus={autoFocus}
							openMenuOnFocus={openMenuOnFocus}
							components={customComponents}
							ref={selectRef}
						/>
						{hasErrorOccurred && (
							<Box paddingBlockStart="space.200">
								<SectionMessage title={errorOccurredHeader} appearance="warning">
									<Button onClick={onRetry} appearance="link" spacing="none">
										{formatMessage(messages.tryAgain)}
									</Button>
								</SectionMessage>
							</Box>
						)}
					</>
				)}
			</Field>
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	marginTop: token('space.300', '24px'),
	width: '100%',
});
